import { useState } from "react";
import { RefreshCw, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Img, Spin } from "@/components/common";

const FIELDS = [["age", "Yaş"], ["gender", "Cinsiyet"], ["hair_color", "Saç rengi"], ["hair_style", "Saç stili"], ["eye_color", "Göz rengi"], ["tone", "Ten"], ["top", "Üst kıyafet"], ["bottom", "Alt kıyafet"], ["shoes", "Ayakkabı"]];

const waitJob = async (id) => {
  for (;;) {
    await new Promise((r) => setTimeout(r, 3000));
    const { data } = await api.get(`/jobs/${id}`);
    if (data.status === "Hazır") return data;
    if (data.status === "Başarısız") throw new Error(data.error || "Üretim başarısız");
  }
};

function CharRow({ c, onChange, onRemove }) {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => onChange({ ...c, [k]: v });
  const regen = async () => {
    if (!window.confirm("1 görsel üretilecek ve ücreti Emergent bakiyenizden düşecek. Onaylıyor musunuz?")) return;
    setBusy(true);
    try {
      await api.patch(`/characters/${c.id}`, pick(c));
      for (const r of c.references || []) await api.delete(`/characters/${c.id}/references/${r.id}`);
      await api.patch(`/characters/${c.id}`, { avatar: "" });
      const { data } = await api.post(`/characters/${c.id}/generate-reference`, { view: "Tam vücut - ön görünüm", count: 1 });
      await waitJob(data.id);
      const { data: fresh } = await api.get(`/characters/${c.id}`);
      onChange(fresh);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border rounded-xl p-3 flex gap-3" data-testid={`review-char-${c.id}`}>
      <div className="w-28 shrink-0 space-y-1.5">
        <div className="relative"><Img src={c.references?.[0]?.url} className="w-28 h-36 rounded-lg" />{busy && <div className="absolute inset-0 bg-white/70 grid place-items-center rounded-lg"><Spin /></div>}</div>
        <button onClick={regen} disabled={busy} className="btn-ghost w-full py-1 text-[11px] flex items-center justify-center gap-1" data-testid={`review-regen-${c.id}`}><RefreshCw size={11} /> {c.references?.length ? "Görseli yenile" : "Görsel üret"}</button>
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex gap-2">
          <input className="inp text-sm py-1.5 font-semibold" value={c.name || ""} onChange={(e) => set("name", e.target.value)} data-testid={`review-name-${c.id}`} />
          <input className="inp text-sm py-1.5" value={c.role || ""} onChange={(e) => set("role", e.target.value)} placeholder="Rol" data-testid={`review-role-${c.id}`} />
          <button onClick={onRemove} className="text-[#d1453b] px-1" title="Karakteri çıkar" data-testid={`review-remove-${c.id}`}><Trash2 size={16} /></button>
        </div>
        <input className="inp text-xs py-1.5" value={(c.aliases || []).join(", ")} onChange={(e) => set("aliases", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="Takma adlar (virgülle)" data-testid={`review-aliases-${c.id}`} />
        <div className="grid grid-cols-3 gap-1.5">{FIELDS.map(([k, l]) => (
          <label key={k} className="block"><span className="text-[10px] text-[#6b7a8c]">{l}</span>
            <input className="inp text-xs py-1" value={c[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`review-${k}-${c.id}`} /></label>))}</div>
        <p className="text-[11px] text-[#8a96a8]">{c.references?.length ? "Görünüşü değiştirdiyseniz \"Görseli yenile\" ile referansı güncelleyin (1 görsel)." : "Referans görseli onaydan sonra bu bilgilere göre çizilecek."}</p>
      </div>
    </div>
  );
}

const pick = (c) => Object.fromEntries(["name", "role", "aliases", ...FIELDS.map((f) => f[0])].map((k) => [k, c[k]]));

export function CharReview({ open, chars, setChars, onApprove, onClose, spend = 0 }) {
  const [saving, setSaving] = useState(false);
  const remove = async (c) => { await api.delete(`/characters/${c.id}`); setChars(chars.filter((x) => x.id !== c.id)); };
  const approve = async () => {
    setSaving(true);
    try {
      for (const c of chars) await api.patch(`/characters/${c.id}`, pick(c));
      onApprove(chars.map((c) => c.id));
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="char-review-dialog">
        <DialogHeader><DialogTitle>Karakter Onayı</DialogTitle></DialogHeader>
        <p className="text-sm text-[#5f6f84] -mt-2">Metinden çıkarılan karakterleri kontrol edin. Sayfalar bu görünüşlerle, her sayfada aynı çizilecek.</p>
        <div className="space-y-3">{chars.map((c) => <CharRow key={c.id} c={c} onChange={(n) => setChars(chars.map((x) => (x.id === c.id ? n : x)))} onRemove={() => remove(c)} />)}
          {!chars.length && <p className="text-sm text-[#8a96a8]">Karakter kalmadı; sayfalar karaktersiz çizilecek.</p>}</div>
        <div className="rounded-lg bg-[#fff4e0] text-[#8a5a12] text-xs px-3 py-2" data-testid="char-review-spend">Onaylarsanız <b>{spend} görsel</b> (karakter referansları, sayfalar ve seçiliyse kapak) üretilecek ve ücreti Emergent bakiyenizden düşecek. Onaylamadan hiçbir görsel üretilmez.</div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="btn-ghost px-4 py-2 text-sm" data-testid="char-review-cancel">Vazgeç</button>
          <button onClick={approve} disabled={saving} className="btn-teal px-5 py-2 text-sm flex items-center gap-2" data-testid="char-review-approve">{saving ? <Spin size={14} /> : <CheckCircle2 size={15} />} Onayla ve {spend} görsel üret</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
